import React, { useEffect, useState } from 'react';
import { useSession } from '../../contexts/SessionContext';

const SessionGuard = ({ children, requiredUserType = null }) => {
  const { isAuthenticated, user, isLoading, validateSession } = useSession();
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    const performValidation = async () => {
      if (!isLoading) {
        setIsValidating(true);
        setValidationError(null);

        try {
          // Check if user is authenticated
          if (!isAuthenticated) {
            console.log('SessionGuard: User not authenticated');
            setValidationError('Not authenticated');
            setIsValidating(false);
            return;
          }

          // Check user type if required
          if (requiredUserType && user?.type !== requiredUserType) {
            console.log(`SessionGuard: User type mismatch. Required: ${requiredUserType}, Current: ${user?.type}`);
            setValidationError(`Access denied. Required: ${requiredUserType}, Current: ${user?.type}`);
            setIsValidating(false);
            return;
          }

          // For vendor users, do basic validation without strict session checks
          if (user?.type === 'vendor') {
            const token = localStorage.getItem('token');
            const vendorData = localStorage.getItem('vendorData');
            
            console.log('SessionGuard: Vendor validation', {
              hasToken: !!token,
              hasVendorData: !!vendorData,
              userType: user?.type
            });

            if (!token || !vendorData) {
              console.log('SessionGuard: Missing vendor token or data');
              setValidationError('Session validation failed');
              setIsValidating(false);
              return;
            }
          } else if (user?.type === 'admin') {
            // For admin, ensure there is a token present
            const adminToken = localStorage.getItem('adminToken');
            const legacyKey = localStorage.getItem('adminApiKey') || localStorage.getItem('ADMIN_API_KEY');
            if (!adminToken && !legacyKey) {
              console.log('SessionGuard: Missing admin token');
              setValidationError('Session validation failed');
              setIsValidating(false);
              return;
            }
            const isValid = await validateSession();
            if (!isValid) {
              console.log('SessionGuard: Session validation failed for admin user');
              setValidationError('Session validation failed');
              setIsValidating(false);
              return;
            }
          } else {
            // Other user types, fallback to validateSession
            const isValid = await validateSession();
            if (!isValid) {
              setValidationError('Session validation failed');
              setIsValidating(false);
              return;
            }
          }

          console.log('SessionGuard: Validation successful');
          setIsValidating(false);
        } catch (error) {
          console.error('Session validation error:', error);
          setValidationError('Session validation error');
          setIsValidating(false);
        }
      }
    };

    performValidation();
  }, [isAuthenticated, user, isLoading, validateSession, requiredUserType]);

  // Show loading state
  if (isLoading || isValidating) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '14px' }}>Validating session...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show error state
  if (validationError) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px',
        padding: '20px'
      }}>
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h3 style={{ color: '#c33', margin: '0 0 12px 0' }}>Access Denied</h3>
          <p style={{ color: '#666', margin: '0 0 16px 0' }}>
            {validationError === 'Not authenticated' 
              ? 'Please log in to access this page.'
              : validationError === 'Session validation failed'
              ? 'Your session has expired. Please log in again.'
              : validationError
            }
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: '#3498db',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Render protected content
  return children;
};

export default SessionGuard;
